"use client";

import { useRef } from "react";
import { useTranslations } from "next-intl";

// Submit button guarded by a confirmation modal (native <dialog>, the
// accessibility best practice: top-layer, focus-trapped, Esc and backdrop-click
// close it). Only an explicit confirm submits the surrounding form — used for
// destructive actions like cancelling bookings or deleting accounts.
export function ConfirmSubmit({
  message,
  title,
  className,
  disabled,
  children,
}: {
  message: string;
  title?: string;
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const t = useTranslations("Common");
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        className={className}
        onClick={() => dialogRef.current?.showModal()}
      >
        {children}
      </button>

      <dialog
        ref={dialogRef}
        onClick={(e) => {
          // Click on the backdrop (the dialog element itself) closes it.
          if (e.target === dialogRef.current) dialogRef.current?.close();
        }}
        className="fixed inset-0 m-auto h-fit w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-sand bg-cream p-6 text-left shadow-xl backdrop:bg-black/50"
      >
        <h3 className="font-display text-lg font-bold text-bark">
          {title ?? t("confirmTitle")}
        </h3>
        <p className="mt-2 text-sm text-stone">{message}</p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            autoFocus
            onClick={() => dialogRef.current?.close()}
            className="rounded-full border border-sand px-4 py-2 text-sm font-medium text-bark hover:border-forest-300"
          >
            {t("goBack")}
          </button>
          <button
            type="button"
            onClick={() => {
              dialogRef.current?.close();
              triggerRef.current?.form?.requestSubmit();
            }}
            className="rounded-full bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            {t("confirm")}
          </button>
        </div>
      </dialog>
    </>
  );
}
